/**
 * Smoke-runnable demo for the S-CP10-4 Timing Defense Gate.
 *
 * Route this anywhere in App.tsx during dev to exercise the full flow
 * without needing a CP10 opp record. Persistence uses the same store as
 * production, but falls back to localStorage if no Supabase session is
 * present.
 */

import { useState } from "react";
import { TimingDefenseGate } from "@/components/cp10/TimingDefenseGate";

const DEMO_DEAL_ID = "00000000-0000-0000-0000-000000000001";
const BLACK = "#0B0B0F";
const BG = "#FAFAF7";

export default function TimingGateSmokeDemo() {
  const [open, setOpen] = useState(false);
  const [closeDate, setCloseDate] = useState("2026-06-30");
  const [composite, setComposite] = useState(72);
  const [lastResult, setLastResult] = useState<string | null>(null);

  return (
    <div style={{ minHeight: "100vh", background: BG, padding: "32px", fontFamily: "ui-sans-serif, system-ui" }}>
      <div style={{ maxWidth: "720px", margin: "0 auto", background: "#fff", borderRadius: "10px", padding: "24px 28px", border: `1px solid ${BLACK}1A` }}>
        <h1 style={{ fontSize: "22px", fontWeight: 700, color: BLACK, marginBottom: "6px" }}>
          S-CP10-4 — Timing Defense Gate (smoke demo)
        </h1>
        <p style={{ fontSize: "13px", color: "#6B6B70", marginBottom: "20px" }}>
          Set a close date, click "Trigger gate". Walk through Q10.TIME.1–4 and the six-button selector.
          Over-call (selecting above the band) shows the justification modal; under-call shows the inline confirm.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
          <label style={{ fontSize: "12px", color: BLACK }}>
            <div style={{ marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.05em", color: "#6B6B70" }}>
              Close date
            </div>
            <input
              type="date"
              value={closeDate}
              onChange={(e) => setCloseDate(e.target.value)}
              style={{ width: "100%", padding: "8px 12px", border: `1px solid ${BLACK}33`, borderRadius: "6px" }}
              data-testid="smoke-close-date"
            />
          </label>
          <label style={{ fontSize: "12px", color: BLACK }}>
            <div style={{ marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.05em", color: "#6B6B70" }}>
              Composite %
            </div>
            <input
              type="number"
              min={0}
              max={100}
              value={composite}
              onChange={(e) => setComposite(Number(e.target.value))}
              style={{ width: "100%", padding: "8px 12px", border: `1px solid ${BLACK}33`, borderRadius: "6px" }}
              data-testid="smoke-composite"
            />
          </label>
        </div>

        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="smoke-trigger-gate"
          style={{
            padding: "10px 20px",
            borderRadius: "6px",
            background: BLACK,
            color: "#fff",
            border: "none",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Trigger gate
        </button>

        {lastResult && (
          <pre
            style={{
              marginTop: "20px",
              padding: "12px 16px",
              background: BG,
              border: `1px solid ${BLACK}1A`,
              borderRadius: "6px",
              fontSize: "12px",
              whiteSpace: "pre-wrap",
            }}
            data-testid="smoke-result"
          >
            {lastResult}
          </pre>
        )}
      </div>

      {open && (
        <TimingDefenseGate
          open={open}
          dealId={DEMO_DEAL_ID}
          accountId={0}
          closeDate={closeDate}
          compositePct={composite}
          onClose={() => setOpen(false)}
          onComplete={({ declaredButton, band, blendedForecastPct }) => {
            setLastResult(
              JSON.stringify(
                {
                  declaredButton,
                  band,
                  blendedForecastPct,
                  compositePct: composite,
                },
                null,
                2,
              ),
            );
          }}
        />
      )}
    </div>
  );
}
